import { c as _c } from "react/compiler-runtime";
import { Chips, DatePicker, NumberInput, Separator, useTranslationContext } from 'platform/components';
import { Space } from 'platform/foundation';
import { HStack, VStack } from 'platform/foundation-tw';
import { match, Pattern } from 'ts-pattern';

// eslint-disable-next-line no-restricted-imports -- Intended
import { always, intersection, isNil, pathOr } from 'ramda';
import { isNotNilOrEmpty, isString } from 'ramda-adjunct';
import { getApiDateString, suffixTestId } from 'shared/web';
import { quickFiltersConst } from '../../constants/quickFilters';
import { useFilterOnChipsChange } from '../../hooks/useFilterOnChipsChange';
import { EagFilterProps } from '../../types/EagFilterProps';
type DateRangeType = {
  from: string | null;
  to: string | null;
};
type RelativeInputType = {
  relativeInputValue: number;
  relativeInputKey: string;
};
export type DateRangeFilterProps = EagFilterProps & {
  min: string | null;
  max: string | null;
  relativeDates: Array<{
    value: string;
    label: string;
  }>;
  relativeInputs: Array<{
    value: string;
    label: string;
  }>;
  quickFilters?: Array<{
    value: string;
    label: string;
  }>;
};
const FILTER_VALUE_KEY = 'value';
export function DateRangeFilter(props) {
  const $ = _c(57);
  const t = useTranslationContext();
  const model = props.value[FILTER_VALUE_KEY] as DateRangeType | RelativeInputType | string | null;
  let t0;
  if ($[0] !== props) {
    t0 = value => {
      const newModel = match(value).with(Pattern.nullish, always(null)).with(Pattern.string, _temp).with({
        from: Pattern.any,
        to: Pattern.any
      }, _temp2).otherwise(always(value));
      props.onChange({
        [FILTER_VALUE_KEY]: newModel
      });
    };
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const setFilterValue = t0;
  let t1;
  if ($[2] !== setFilterValue) {
    t1 = {
      setFilterValue,
      defaultValue: null
    };
    $[2] = setFilterValue;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    onChipsChange
  } = useFilterOnChipsChange(t1);
  let t2;
  if ($[4] !== model) {
    t2 = pathOr(null, ["from"], model);
    $[4] = model;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const fromPickerValue = t2;
  let t3;
  if ($[6] !== model) {
    t3 = pathOr(null, ["to"], model);
    $[6] = model;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const toPickerValue = t3;
  const inputValue = isRelativeValue(model) ? model : null;
  let t4;
  if ($[8] !== model) {
    t4 = isString(model) ? [model] : [];
    $[8] = model;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const chipsValue = t4;
  let t5;
  if ($[10] !== model || $[11] !== setFilterValue) {
    t5 = value_0 => {
      setFilterValue({
        from: value_0 ? getApiDateString(value_0) : null,
        to: pathOr(null, ["to"], model)
      });
    };
    $[10] = model;
    $[11] = setFilterValue;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const onDateFromChange = t5;
  let t6;
  if ($[13] !== model || $[14] !== setFilterValue) {
    t6 = value_1 => {
      setFilterValue({
        to: value_1 ? getApiDateString(value_1) : null,
        from: pathOr(null, ["from"], model)
      });
    };
    $[13] = model;
    $[14] = setFilterValue;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const onDateToChange = t6;
  let t7;
  if ($[16] !== setFilterValue) {
    t7 = (relativeInputValue, relativeInputKey) => {
      const newModel_0 = isNaN(relativeInputValue) || relativeInputValue === 0 ? null : {
        relativeInputValue,
        relativeInputKey
      };
      setFilterValue(newModel_0);
    };
    $[16] = setFilterValue;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const onInputChange = t7;
  let t8;
  if ($[18] !== model) {
    t8 = intersection([model], quickFiltersConst);
    $[18] = model;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const isQuickFilterValue = t8 as string[];
  let t9;
  if ($[20] !== isQuickFilterValue || $[21] !== onChipsChange || $[22] !== props) {
    t9 = props.quickFilters && props.quickFilters.length > 0 && <><HStack><Chips isDisabled={props.isDisabled} value={isQuickFilterValue} options={props.quickFilters} onChange={onChipsChange} isMultiple={false} data-testid={suffixTestId("quickFilters-chips", props)} isDeselectable={true} /></HStack><Separator /></>;
    $[20] = isQuickFilterValue;
    $[21] = onChipsChange;
    $[22] = props;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== chipsValue || $[25] !== onChipsChange || $[26] !== props) {
    t10 = props.relativeDates && props.relativeDates.length > 0 && <><Chips isDisabled={props.isDisabled} value={chipsValue} options={props.relativeDates} onChange={onChipsChange} isMultiple={false} data-testid={suffixTestId("relativeDates-chips", props)} isDeselectable={true} /><Separator /></>;
    $[24] = chipsValue;
    $[25] = onChipsChange;
    $[26] = props;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t) {
    t11 = t("page.datagrid.filter.dateLabelFrom");
    $[28] = t;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== props) {
    t12 = suffixTestId("date-range-filter-from", props);
    $[30] = props;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== fromPickerValue || $[33] !== onDateFromChange || $[34] !== t11 || $[35] !== t12) {
    t13 = <DatePicker label={t11} value={fromPickerValue} onChange={onDateFromChange} data-testid={t12} />;
    $[32] = fromPickerValue;
    $[33] = onDateFromChange;
    $[34] = t11;
    $[35] = t12;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Space vertical={2} />;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== t) {
    t15 = t("page.datagrid.filter.dateLabelTo");
    $[38] = t;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== props) {
    t16 = suffixTestId("date-range-filter-to", props);
    $[40] = props;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== onDateToChange || $[43] !== t15 || $[44] !== t16 || $[45] !== toPickerValue) {
    t17 = <DatePicker label={t15} value={toPickerValue} onChange={onDateToChange} data-testid={t16} />;
    $[42] = onDateToChange;
    $[43] = t15;
    $[44] = t16;
    $[45] = toPickerValue;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== inputValue || $[48] !== onInputChange || $[49] !== props) {
    t18 = props.relativeInputs.map(relativeInput => <NumberInput key={relativeInput.value} label={relativeInput.label} isDisabled={props.isDisabled} onChange={value_2 => onInputChange(value_2 ?? 0, relativeInput.value)} value={relativeInput.value === inputValue?.relativeInputKey ? inputValue.relativeInputValue : 0} data-testid={suffixTestId(`relativeInput-${relativeInput.label}`, props)} />);
    $[47] = inputValue;
    $[48] = onInputChange;
    $[49] = props;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  let t19;
  if ($[51] !== t10 || $[52] !== t13 || $[53] !== t17 || $[54] !== t18 || $[55] !== t9) {
    t19 = <VStack spacing={2}>{t9}{t10}{t13}{t14}{t17}{t18}</VStack>;
    $[51] = t10;
    $[52] = t13;
    $[53] = t17;
    $[54] = t18;
    $[55] = t9;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  return t19;
}
function _temp2(objectValue) {
  if (isNil(objectValue.from) && isNil(objectValue.to)) {
    return null;
  }
  return objectValue;
}
function _temp(stringValue) {
  return stringValue;
}
const isRelativeValue = (model: unknown): model is RelativeInputType => isNotNilOrEmpty((model as RelativeInputType)?.relativeInputKey);